/**
 * 位置情報を取得できなかった理由。
 * 「許可されていない（打つ手＝設定変更）」と「測位できなかった（打つ手＝再試行・場所を変える）」で
 * 案内すべき内容が変わるため、available: false を 1 つにまとめず理由を保持する。
 *
 * null は「測位に成功した」または「そもそも測位していない」を表す（区別は available で行う）。
 */
export type GpsFailureReason =
  /** 権限拒否（GeolocationPositionError.code 1）。待っても状況が変わらない */
  | 'PERMISSION_DENIED'
  /** 測位不能（code 2）。端末の位置情報サービスOFF・電波不良 */
  | 'POSITION_UNAVAILABLE'
  /** 期限内に測位が届かなかった（code 3、および精度ゲートの打ち切り） */
  | 'TIMEOUT'
  /** Geolocation API 非対応 */
  | 'UNSUPPORTED'

/**
 * 測位失敗の理由を案内文言の i18n キーに変換する（#3541）。
 *
 * v1（`frontend/src/pages/[campaign]/spot/detail.tsx:77-84`、削除コミット 91831998d）が
 * GeolocationPositionError.code で出し分けていたマッピングの復元。
 * v2 のリプレイスで PERMISSION_DENIED 以外を握り潰したため、原因の分からない
 * 汎用文言しか出せなくなっていた。キーは全対応言語で既存のものをそのまま使う。
 */
export const resolveGpsFailureMessageKey = (reason: GpsFailureReason | null): string => {
  switch (reason) {
    // v1 の access_error_message_02（「位置情報の使用許可がおりませんでした。」）ではなく、
    // 同じ画面の獲得ボタンのラベル（SpotDetail の stampGpsPermissionDenied）と同じ文言を使う。
    // 原因の説明より「何をすればよいか」を示すほうが行動につながり、ボタンと表示も揃う
    case 'PERMISSION_DENIED':
      return 'spot_detail.gps_permission_denied'
    case 'POSITION_UNAVAILABLE':
      return 'spot.access_error_message_03'
    case 'TIMEOUT':
      return 'spot.access_error_message_04'
    case 'UNSUPPORTED':
      return 'spot.access_error_geo'
    // 理由が特定できなかった場合（測位を試みずにここへ来た場合を含む）。v1 の code 0 に相当する
    default:
      return 'spot.access_error_message_01'
  }
}

/**
 * 再試行しても状況が変わらない理由か（＝再試行導線を出さない）。
 * 権限拒否は設定を変えない限り同じ結果になり、非対応は端末側の制約で変わらない。
 */
export const isUnrecoverableGpsFailure = (reason: GpsFailureReason | null): boolean =>
  reason === 'PERMISSION_DENIED' || reason === 'UNSUPPORTED'
